/**
 * Hook for calculating derived metrics from positions.
 * Memoizes expensive calculations for performance.
 *
 * Only calculates metrics that are meaningful without net liquidating value:
 * - Realized P&L from closed positions
 * - Win rate based on actual trade outcomes
 * - Position counts and sizes
 * - Concentration/risk metrics
 *
 * All metrics are scoped to a rolling 30-day window for scalability.
 */

import { useMemo } from 'react';
import type { Position, PortfolioMetrics, RiskMetrics } from '@/types';

/** Rolling window in days for metric calculations. */
const ROLLING_WINDOW_DAYS = 30;

/**
 * Filter positions to those relevant within the rolling window.
 * - Active positions: included if updated within the window
 * - Closed positions: included if closed within the window
 */
function getPositionsInWindow(positions: Position[], windowDays: number): Position[] {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - windowDays);
  const cutoffTime = cutoff.getTime();

  return positions.filter((p) => {
    if (p.position_status === 'ACTIVE') {
      return new Date(p.updated_at).getTime() >= cutoffTime;
    }
    if (p.position_status === 'CLOSED' && p.closed_at) {
      return new Date(p.closed_at).getTime() >= cutoffTime;
    }
    return false;
  });
}

/**
 * Calculate portfolio metrics from positions (rolling 30-day window).
 * Only includes metrics derivable from position data without NLV,
 * except for largest_position_percent which requires NLV.
 */
export function calculatePortfolioMetrics(positions: Position[], nlv?: number | null): PortfolioMetrics {
  const windowPositions = getPositionsInWindow(positions, ROLLING_WINDOW_DAYS);
  const activePositions = windowPositions.filter((p) => p.position_status === 'ACTIVE');
  const closedPositions = windowPositions.filter((p) => p.position_status === 'CLOSED');

  // Realized P&L: sum from closed positions in the window
  const totalRealizedPnl = closedPositions.reduce((sum, p) => sum + p.realized_pnl, 0);

  // Win rate: based on closed positions only (actual outcomes)
  const winsCount = closedPositions.filter((p) => p.realized_pnl > 0).length;
  const winRate = closedPositions.length > 0
    ? (winsCount / closedPositions.length) * 100
    : 0;

  // Active position metrics
  const totalCostBasis = activePositions.reduce((sum, p) => sum + p.cost_basis, 0);
  const avgPositionSize = activePositions.length > 0
    ? totalCostBasis / activePositions.length
    : 0;

  // Largest position percentage (uses NLV as denominator)
  let largestPositionPercent = 0;
  let concentratedPositions = 0;

  if (nlv && nlv > 0 && activePositions.length > 0) {
    const largestValue = Math.max(...activePositions.map((p) => p.cost_basis));
    largestPositionPercent = (largestValue / nlv) * 100;

    // Concentrated positions (>10% of NLV)
    const concentrationThreshold = 0.1;
    concentratedPositions = activePositions.filter(
      (p) => p.cost_basis / nlv > concentrationThreshold
    ).length;
  }

  return {
    total_realized_pnl: totalRealizedPnl,
    win_rate: winRate,
    open_position_count: activePositions.length,
    avg_position_size: avgPositionSize,
    largest_position_percent: largestPositionPercent,
    concentrated_positions: concentratedPositions,
    closed_count: closedPositions.length,
    wins_count: winsCount,
  };
}

/**
 * Calculate risk metrics from positions (rolling 30-day window).
 * Uses NLV as denominator when available; returns zeros otherwise.
 */
export function calculateRiskMetrics(
  positions: Position[],
  concentrationThreshold: number = 10,
  nlv?: number | null
): RiskMetrics {
  const windowPositions = getPositionsInWindow(positions, ROLLING_WINDOW_DAYS);
  const activePositions = windowPositions.filter((p) => p.position_status === 'ACTIVE');

  if (activePositions.length === 0 || !nlv || nlv <= 0) {
    return {
      largest_position_percent: 0,
      largest_position_symbol: '-',
      top5_concentration: 0,
      positions_over_threshold: 0,
      concentrated_symbols: [],
    };
  }

  const positionsWithPercent = activePositions
    .map((p) => ({
      symbol: p.symbol,
      percent: (p.cost_basis / nlv) * 100,
    }))
    .sort((a, b) => b.percent - a.percent);

  const largest = positionsWithPercent[0];
  const top5Sum = positionsWithPercent.slice(0, 5).reduce((sum, p) => sum + p.percent, 0);
  const concentrated = positionsWithPercent.filter((p) => p.percent > concentrationThreshold);

  return {
    largest_position_percent: largest?.percent ?? 0,
    largest_position_symbol: largest?.symbol ?? '-',
    top5_concentration: top5Sum,
    positions_over_threshold: concentrated.length,
    concentrated_symbols: concentrated.map((p) => p.symbol),
  };
}

/**
 * Hook for calculating memoized portfolio metrics.
 */
export function useMetrics(positions: Position[], nlv?: number | null) {
  const portfolioMetrics = useMemo(
    () => calculatePortfolioMetrics(positions, nlv),
    [positions, nlv]
  );

  const riskMetrics = useMemo(
    () => calculateRiskMetrics(positions, 10, nlv),
    [positions, nlv]
  );

  return {
    portfolioMetrics,
    riskMetrics,
  };
}

/**
 * Calculate allocation data for charts (rolling 30-day window, active positions only).
 * Uses NLV as denominator when available, otherwise falls back to total cost basis.
 */
export function calculateAllocations(
  positions: Position[],
  nlv?: number | null
): Array<{ symbol: string; value: number; percent: number }> {
  const windowPositions = getPositionsInWindow(positions, ROLLING_WINDOW_DAYS);
  const activePositions = windowPositions.filter((p) => p.position_status === 'ACTIVE');
  const totalCostBasis = activePositions.reduce((sum, p) => sum + p.cost_basis, 0);

  // Use NLV if available, otherwise fall back to total cost basis for allocation chart
  const denominator = nlv && nlv > 0 ? nlv : totalCostBasis;

  if (denominator === 0) return [];

  return activePositions
    .map((p) => ({
      symbol: p.symbol,
      value: p.cost_basis,
      percent: (p.cost_basis / denominator) * 100,
    }))
    .sort((a, b) => b.value - a.value);
}
